import { IsBoolean, IsIn, IsISO8601, IsOptional, IsString, MaxLength } from 'class-validator';
import { PREPACKAGED_MARKET_COUNTRY_CODES } from '../../label/prepackaged-country-names';
import {
  IsIdentificationMarkApprovalNumber,
  IsIdentificationMarkCountryCode,
} from '../../validation/is-identification-mark.decorator';
import { IsPartitaIvaFormat } from '../../validation/is-partita-iva.decorator';
import { IsPhone } from '../../validation/is-phone';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';

export class UpdateTenantProfileDto {
  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  name?: string;

  @IsISO8601({ strict: true })
  @IsOptional()
  expectedUpdatedAt?: string;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  ragioneSociale?: string;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  @IsPartitaIvaFormat()
  partitaIva?: string;

  @IsBoolean()
  @IsOptional()
  isMultiSite?: boolean;

  /**
   * Tri-statale (vedi `phone` sotto): assente/undefined = non toccare,
   * `null` = cancella, stringa = imposta. `@IsOptional()` salta tutti i
   * validatori successivi (incluso `@IsString()`) quando il valore è `null`.
   */
  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  codiceFiscale?: string | null;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  indirizzoVia?: string;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  indirizzoCap?: string;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  indirizzoCitta?: string;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  indirizzoProvincia?: string;

  /**
   * Tri-statale (vedi `phone` sotto). Caso grave se non rispettato: un
   * Codice SDI/PEC sbagliato inseriti in fatturazione elettronica devono
   * poter essere cancellati, non solo sovrascritti.
   */
  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  pec?: string | null;

  /** Tri-statale (vedi `phone` sotto) — vedi nota su `pec`. */
  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  codiceSdi?: string | null;

  /**
   * Telefono di riferimento, cellulare o fisso. Tri-statale: assente/undefined
   * (o '') = non toccare, `null` = cancella il numero, stringa = imposta
   * (normalizzata E.164 lato service). Il frontend manda `null` quando
   * l'utente svuota il campo, perché il mapping del service salta le
   * proprietà undefined e un '' verrebbe ignorato in silenzio.
   */
  @IsOptional()
  @OptionalStringTransform()
  @IsPhone()
  phone?: string | null;

  /** ISO 3166-1 alpha-2 della sede legale. Tri-statale (vedi `phone` sopra). */
  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  @IsIn(PREPACKAGED_MARKET_COUNTRY_CODES)
  countryCode?: string | null;

  /**
   * Sede dello stabilimento di produzione o, se diverso, di confezionamento
   * (art. 4 c. 1 D.Lgs. 145/2017) — diversa dalla sede legale sopra. Default
   * per l'etichetta preimballata; il progetto puo' sovrascriverla. Tri-statale
   * (vedi `phone` sopra).
   */
  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  @MaxLength(500)
  establishmentAddress?: string | null;

  /**
   * Codice paese stampato nel marchio ovale di identificazione sanitaria
   * (all. II sez. I parte B punto 6 Reg. CE 853/2004). Rilevante solo per chi
   * gestisce uno stabilimento riconosciuto per prodotti di origine animale.
   * Tri-statale (vedi `phone` sopra).
   */
  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  @IsIdentificationMarkCountryCode()
  establishmentCountryCode?: string | null;

  /**
   * Numero di riconoscimento dello stabilimento (art. 4 Reg. CE 853/2004),
   * es. "1234" nel bollo "IT 1234 CE". Tri-statale (vedi `phone` sopra).
   */
  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  @IsIdentificationMarkApprovalNumber()
  establishmentApprovalNumber?: string | null;
}
